import { fetchEventSource } from '@microsoft/fetch-event-source';
import { z } from 'zod';

import { MessageResponseSchema } from '@voll-intelligence/domain';
import { env } from '@web/core';

import type { AuthService } from '../auth/auth_service';
import { trimTrailingSlashes } from '../url';
import { createSseRetryPolicy } from './sse_retry';

const ChatPushEnvelopeSchema = z.object({
  v: z.literal(1),
  messages: z.array(MessageResponseSchema),
});

export interface ConversationStreamClient {
  /**
   * Subscribes to the per-conversation SSE stream. Returns an unsubscribe
   * function that aborts the connection. Auto-reconnects with backoff (the
   * underlying `fetchEventSource`); malformed frames are dropped.
   */
  subscribe: (
    conversationId: string,
    onMessages: (messages: z.infer<typeof MessageResponseSchema>[]) => void,
  ) => () => void;
}

/**
 * SSE client for the conversation chat-push stream. Mirrors `createSseClient`:
 * defaults to the conversation service base URL and pulls the bearer token from
 * the same `authService`. `EventSource` can't set an `Authorization` header, so
 * we use fetch-based SSE (`@microsoft/fetch-event-source`).
 */
export function createConversationStreamClient(
  authService: AuthService,
  baseUrl: string = env.VITE_CONVERSATION_API_URL,
): ConversationStreamClient {
  return {
    subscribe: (conversationId, onMessages) => {
      const ctrl = new AbortController();
      // Bounds fetchEventSource's default forever-every-1s reconnect: stops on a
      // fatal 4xx, backs off exponentially otherwise, and gives up after a few
      // consecutive failures (see sse_retry.ts).
      const retry = createSseRetryPolicy();

      void (async () => {
        try {
          const token = await authService.getAccessToken();
          if (ctrl.signal.aborted) return;
          const url = `${trimTrailingSlashes(baseUrl)}/conversations/${encodeURIComponent(conversationId)}/stream`;
          await fetchEventSource(url, {
            signal: ctrl.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            // Keep streaming while the tab is backgrounded (operators leave it open).
            openWhenHidden: true,
            onopen: retry.onopen,
            onerror: retry.onerror,
            onmessage: (ev) => {
              if (!ev.data) return; // keep-alive comments carry no data
              let payload: unknown;
              try {
                payload = JSON.parse(ev.data);
              } catch {
                return; // drop malformed frames instead of throwing
              }
              const parsed = ChatPushEnvelopeSchema.safeParse(payload);
              if (parsed.success) onMessages(parsed.data.messages);
            },
          });
        } catch {
          // Swallow abort/network rejections — unsubscribe aborts the fetch,
          // which would otherwise surface as an unhandled rejection.
        }
      })();

      return () => {
        ctrl.abort();
      };
    },
  };
}
